import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Files, PenSquare, SearchX, TriangleAlert } from 'lucide-react';
import type { PlatformId, PostStatus } from '../../shared/types';
import { isPlatformId, isPostStatus } from '../../shared/types';
import { PageHeader } from '../components/layout';
import { Button, Card, EmptyState, Skeleton } from '../components/ui';
import { PostFilterBar } from '../components/posts/PostFilterBar';
import type { PostFilters } from '../components/posts/PostFilterBar';
import { PostListRow } from '../components/posts/PostListRow';
import { PostDetailDrawer } from '../components/posts/PostDetailDrawer';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useWorkspaceStore, useWorkspaceTimezone } from '../stores/workspace';

const PAGE_SIZE = 20;

function readFilters(params: URLSearchParams): PostFilters {
  const status = params.get('status') ?? '';
  const platform = params.get('platform') ?? '';
  return {
    status: isPostStatus(status) ? status : '',
    platform: isPlatformId(platform) ? platform : '',
    search: params.get('q') ?? '',
  };
}

/**
 * Posts — owned by WEB-3. Filterable, paginated list with a detail drawer.
 */
export function PostsPage() {
  const navigate = useNavigate();
  const { postId: routePostId } = useParams<{ postId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const timezone = useWorkspaceTimezone();

  const filters = readFilters(searchParams);
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(routePostId ?? null);

  // Deep-linked /posts/:postId opens the drawer directly.
  useEffect(() => {
    if (routePostId) setSelectedPostId(routePostId);
  }, [routePostId]);

  const setFilters = (next: PostFilters) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (next.status) params.set('status', next.status);
      else params.delete('status');
      if (next.platform) params.set('platform', next.platform);
      else params.delete('platform');
      if (next.search) params.set('q', next.search);
      else params.delete('q');
      return params;
    });
    setCursorHistory([undefined]);
    setPageIndex(0);
  };

  const currentCursor = cursorHistory[pageIndex];

  const query = useQuery({
    queryKey: [
      ...qk.posts(workspaceId, {
        status: (filters.status || undefined) as PostStatus | undefined,
        platform: (filters.platform || undefined) as PlatformId | undefined,
        search: filters.search || undefined,
      }),
      currentCursor ?? 'first',
    ],
    queryFn: () =>
      api.posts.list({
        status: filters.status || undefined,
        platform: filters.platform || undefined,
        search: filters.search || undefined,
        cursor: currentCursor,
        limit: PAGE_SIZE,
      }),
    enabled: Boolean(workspaceId),
    placeholderData: (previous) => previous,
  });

  const posts = query.data?.posts ?? [];
  const nextCursor = query.data?.nextCursor ?? null;
  const hasActiveFilters = filters.status !== '' || filters.platform !== '' || filters.search !== '';

  const goNext = () => {
    if (!nextCursor) return;
    setCursorHistory((history) => {
      if (pageIndex + 1 < history.length) return history;
      return [...history, nextCursor];
    });
    setPageIndex((index) => index + 1);
  };

  const goPrev = () => setPageIndex((index) => Math.max(0, index - 1));

  const closeDrawer = () => {
    setSelectedPostId(null);
    if (routePostId) navigate('/posts', { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Posts"
        description="Every post and its per-platform status, from draft to published."
        actions={
          <Button leftIcon={<PenSquare className="size-4" />} onClick={() => navigate('/composer')}>
            New post
          </Button>
        }
      >
        <PostFilterBar value={filters} onChange={setFilters} />
      </PageHeader>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4 px-4 py-4">
                <Skeleton className="size-2 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState
            variant="plain"
            icon={<TriangleAlert className="size-5" />}
            title="Could not load your posts"
            description={
              query.error instanceof ApiError ? query.error.message : 'Try again in a moment.'
            }
            action={
              <Button variant="secondary" onClick={() => void query.refetch()}>
                Try again
              </Button>
            }
          />
        ) : posts.length === 0 ? (
          <EmptyState
            variant="plain"
            icon={hasActiveFilters ? <SearchX className="size-5" /> : <Files className="size-5" />}
            title={hasActiveFilters ? 'No posts match these filters' : 'No posts yet'}
            description={
              hasActiveFilters
                ? 'Try a different status, platform or search term.'
                : 'Posts you draft, schedule or publish show up here with a status chip per platform.'
            }
            action={
              hasActiveFilters ? (
                <Button variant="secondary" onClick={() => setFilters({ status: '', platform: '', search: '' })}>
                  Clear filters
                </Button>
              ) : (
                <Button onClick={() => navigate('/composer')}>Write a post</Button>
              )
            }
          />
        ) : (
          <div>
            {posts.map((post) => (
              <PostListRow
                key={post.id}
                post={post}
                timezone={timezone}
                onOpen={(id) => setSelectedPostId(id)}
              />
            ))}
          </div>
        )}
      </Card>

      {(pageIndex > 0 || nextCursor) && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-xs text-subtle">
            Page {pageIndex + 1}
            {query.isFetching && ' · loading…'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ChevronLeft className="size-3.5" />}
              disabled={pageIndex === 0}
              onClick={goPrev}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              rightIcon={<ChevronRight className="size-3.5" />}
              disabled={!nextCursor}
              onClick={goNext}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <PostDetailDrawer postId={selectedPostId} onClose={closeDrawer} />
    </>
  );
}

export default PostsPage;
